import {Injectable} from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import {Location} from '@angular/common';
import {Globals} from '../../shared/globals/globals';
import {SubDivisionService} from '../subDivision/sub-division.service';
import {LoginService} from './login.service';

/** This guard is used in the oauth callback flow, consuming the URL before the Angular router can.
 * The URL built by the auth provider is technically invalid (no ? for the queryParams), so this guard
 * needs to consume the URL before Angular's router (which would fail to parse it).
 */
@Injectable()
export class UrlConsumerService  {

	/** Creates an instance of the UrlConsumerService
	 *
	 * @param router
	 * @param location
	 * @param subDivisionService
	 * @param globals
	 */
	constructor(private router: Router
		  	  , private location: Location
			  , private subDivisionService: SubDivisionService
			  , public globals: Globals
			  , private loginService: LoginService) {
				this.router = router;
				this.location = location;
				this.subDivisionService = subDivisionService;
				this.globals = globals;
				this.loginService = loginService;
	}

	/** the actual guard fuction. Parses the queryString and stores the params in sessionStorage.
	 * Redirects the user to the default route, or to the route that was stored before the auth redirect.
	 *
	 * @param {ActivatedRouteSnapshot} childRoute
	 * @param {RouterStateSnapshot} state
	 * @returns {boolean} whether route can be activated or not
	 */
	canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
		const queryParamsObj = this.getQueryParams();

		if (queryParamsObj && queryParamsObj['error']) {
			
			if (this.loginService['pkceService']) {
				this.loginService['pkceService'].clearPkceValues();
			}
			
			this.router.navigate(['no-access']);
			return false;
		}

		if (queryParamsObj && queryParamsObj['code'] != null) {
			localStorage.setItem('authCode', queryParamsObj['code']);
			
			if (queryParamsObj['state']) {
				localStorage.setItem('authState', queryParamsObj['state']);
			}
			
			this.loginService.exchangeCodeForToken(queryParamsObj['code']).subscribe({
				next: (tokenResponse) => {
					this.loginService.processTokenResponse(tokenResponse);
					this.handleSuccessfulAuth();
				},
				error: (error) => {
					console.error('Token exchange failed:', error);
					this.router.navigate(['no-access']);
				}
			});
			
			return false;
		}
		
		else if (queryParamsObj && queryParamsObj['access_token'] != null) {
			const accessToken = atob(queryParamsObj['access_token'].split('.')[1]);
			const jsonToken = JSON.parse(accessToken);

			localStorage.setItem('strAccessToken', accessToken);
			localStorage.setItem('wingsEncodedAccessToken', queryParamsObj['access_token']);

			localStorage.setItem('tokenIssue', jsonToken.iat);
			localStorage.setItem('tokenExp', jsonToken.exp);
			this.handleSuccessfulAuth();
			return false;
		}

		else {
			console.error('No authorization code or access token found');
		}

		return false;
	}

	/** Parses the queryString or hash to pick off OAuth parameters.
	 * @returns {Object | null} The OAuth params in Object format, or null if none found.
	 */
	getQueryParams() {
		let queryString = '';
		
		if (window.location.search && (window.location.search.indexOf('code') !== -1 || window.location.search.indexOf('access_token') !== -1)) {
			queryString = window.location.search.substring(1);
		}
		else if (window.location.hash && (window.location.hash.indexOf('access_token') !== -1 || window.location.hash.indexOf('code') !== -1)) {
			queryString = window.location.hash.substring(1);
		}
		else if (this.location.path(true).indexOf('access_token') !== -1) {
			queryString = this.location.path(true);
		}
		
		if (queryString) {
			
			const paramArray = queryString.split('&');
			const queryParamsObj = new Object();

			for (const param of paramArray) {
				
				const i = param.indexOf('=');
				if (i > 0) {
					const key = param.slice(0, i);
					const value = param.slice(i + 1);
					queryParamsObj[key] = decodeURIComponent(value);
				}
			}

			return queryParamsObj;
		}

		console.log('No OAuth parameters found');
		return null;
	}

	/**
	 * Handle successful authentication by redirecting to the appropriate page
	 */
	private handleSuccessfulAuth(): void {
		if (localStorage.getItem('redirectURL')) {
			const redirectUrl = localStorage.getItem('redirectURL');
			localStorage.removeItem('redirectURL');
			
			if (redirectUrl && redirectUrl !== 'error' && !redirectUrl.includes('error')) {
				this.router.navigate([redirectUrl]);
			} else {
				this.router.navigate(['home-page']);
			}
		} else {
			console.log('No redirect URL stored, navigating to home-page');
			this.router.navigate(['home-page']);
		}
	}
}
